import * as React from 'react';
import { useParams } from 'react-router';
import type { RequirementId } from '@gcv/shared';
import { CommentType, RequirementType } from '@gcv/shared';
import { InfoOutlined } from '@mui/icons-material';
import Help from '@mui/icons-material/Help';
import { Box, useMediaQuery } from '@mui/material';

import { ReactComponent as GreenTickIcon } from 'assets/images/ic-circle-checkmark-green.svg';
import { ReactComponent as UnfulfilledIcon } from 'assets/images/ic-circle-checkmark-grey.svg';
import { useComponent } from 'hooks';
import { useInjection } from 'ioc';
import { Button, CommentBadge, Pill, Text, Tooltip } from 'ui';
import { OnboardingPresenter } from 'ui/apps/crb/onboarding-two/onboarding.presenter';
import { theme } from 'ui/theme';
import { isOnboardingDocumentRequirementFulfilled } from 'util/due-diligence.util';
import { RequirementActionsContainer } from './components/requirement-actions-container';

export interface RequirementCardProps extends Record<string, unknown> {
  requirementId: RequirementId;
}

export const RequirementCardV2: React.FC<RequirementCardProps> = useComponent(({ requirementId }) => {
  const presenter = useInjection(OnboardingPresenter);
  const [loadingAttachments, setLoadingAttachments] = React.useState(false);
  const requirement = presenter.viewModel.onboardingTemplate.requirements[requirementId];
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const result = presenter.viewModel.templateResults.requirements_results.find(
    (r) => requirement && r.requirement_id === requirement.requirement_id
  );

  const { bank_id } = useParams() as { bank_id: string };

  if (!requirement) {
    return <></>;
  }

  const isMarijuanaLicense = requirement.type === RequirementType.BusinessLicense;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile || isTablet ? 'column' : 'row',
        gap: 3,
        border: 1,
        borderRadius: 2,
        padding: 3,
        backgroundColor: isMarijuanaLicense ? 'rgba(3, 166, 91, 0.07)' : theme.palette.primary.contrastText,
        borderColor: isMarijuanaLicense ? theme.palette.primary.main : theme.palette.divider,
        justifyContent: 'space-between'
      }}
      data-cy="requirement-card"
    >
      <Box sx={{ display: 'flex', gap: 2, padding: 1 }}>
        <Box sx={{ fontSize: '22px' }}>
          {isOnboardingDocumentRequirementFulfilled(result) ? <GreenTickIcon /> : <UnfulfilledIcon />}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, margin: 0 }}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: isMobile ? 'space-between' : undefined }}>
            <Tooltip
              placement="top"
              title={requirement.name}
              disabled={requirement.name.replace(/ /g, '').length > 60 ? false : true}
            >
              <Text
                content={requirement.name}
                dataCy="requirement-name"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '60ch',
                  fontSize: '18px',
                  fontWeight: 700,
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2
                }}
              />
            </Tooltip>
            <CommentBadge
              post={{
                type: CommentType.REQUIREMENT,
                title: requirement.name,
                idComponents: {
                  requirementId: requirementId,
                  crbId: presenter.crbDispensaryStore.currentDispensary.id,
                  fiId: bank_id
                }
              }}
              thinIcon
            />
            {isMarijuanaLicense && (
              <Pill text={presenter.viewModel.isBusinessLicenseRequired ? 'Required' : 'Optional'} />
            )}
          </Box>
          <Box>
            <Text content={requirement.description} dataCy="requirement-description" />
            {requirement?.attachments && requirement.attachments?.length > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  label={'Download Attachment'}
                  color="text"
                  isLoading={loadingAttachments}
                  onClick={() => {
                    setLoadingAttachments(true);
                    presenter.downloadRequirementAttachments(requirement).finally(() => {
                      setLoadingAttachments(false);
                    });
                  }}
                  style={{ textDecoration: 'underline', padding: '0' }}
                />

                <Tooltip
                  variant="text"
                  title={
                    'Your financial institution attached files to help you fulfill this requirement. Click to download.'
                  }
                  placement="top"
                >
                  <Help color="primary" />
                </Tooltip>
              </Box>
            ) : null}
          </Box>

          {isMarijuanaLicense && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                border: '1px solid #C6CCDA;',
                borderRadius: 2,
                padding: 2,
                backgroundColor: 'white',
                borderColor: theme.palette.primary.main,
                maxWidth: '700px'
              }}
            >
              <InfoOutlined sx={{ fontSize: '16px', color: theme.palette.primary.main, marginTop: '4px' }} />
              <Text
                content={
                  presenter.viewModel.isBusinessLicenseRequired
                    ? 'Based on the information you provided in Business Details, please upload a copy of your marijuana license. This document is required to complete your application and confirm your authorization to operate under your state’s cannabis regulations.'
                    : 'If you already have a license or provisional approval from your state regulator, upload a copy here. If you don’t yet have one, you can skip this step and upload it once your license is issued.'
                }
                type="body2"
                sx={{ color: theme.palette.primary.dark }}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isMobile || isTablet ? 'center' : 'flex-start',
          marginBottom: 'auto'
        }}
      >
        <RequirementActionsContainer result={result} requirement={requirement} />
      </Box>
    </Box>
  );
});
